import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { 
  getUpdatesAdmin, 
  createUpdate, 
  updateUpdate, 
  deleteUpdate, 
  toggleUpdateVisibility 
} from '../services/api';
import { useAuth } from '../context/AuthContext';

function AdminUpdates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    titre: '',
    description: '',
    type: 'mineur',
    nouveautes: [''],
    ameliorations: [''],
    corrections: [''],
    visible: true
  });

  useEffect(() => {
    // Vérifier si l'utilisateur est admin
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUpdates();
  }, [user, navigate]);

  const fetchUpdates = async () => {
    try {
      const data = await getUpdatesAdmin();
      setUpdates(data);
    } catch (error) {
      console.error('Erreur lors du chargement des updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const resetForm = () => {
    setFormData({
      version: '',
      titre: '',
      description: '',
      type: 'mineur',
      nouveautes: [''],
      ameliorations: [''],
      corrections: [''],
      visible: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filtrer les champs vides des tableaux
    const cleanedData = {
      ...formData,
      nouveautes: formData.nouveautes.filter(item => item.trim() !== ''),
      ameliorations: formData.ameliorations.filter(item => item.trim() !== ''),
      corrections: formData.corrections.filter(item => item.trim() !== '')
    };

    try {
      if (editingId) {
        await updateUpdate(editingId, cleanedData);
      } else {
        await createUpdate(cleanedData);
      }
      
      fetchUpdates();
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (update) => {
    setFormData({
      version: update.version,
      titre: update.titre,
      description: update.description,
      type: update.type,
      nouveautes: update.nouveautes.length > 0 ? update.nouveautes : [''],
      ameliorations: update.ameliorations.length > 0 ? update.ameliorations : [''],
      corrections: update.corrections.length > 0 ? update.corrections : [''],
      visible: update.visible
    });
    setEditingId(update._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet update ?')) return;
    
    try {
      await deleteUpdate(id);
      fetchUpdates();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const toggleVisibility = async (id) => {
    try {
      await toggleUpdateVisibility(id);
      fetchUpdates();
    } catch (error) {
      console.error('Erreur lors du changement de visibilité:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              🔐 Gestion des Updates
            </h1>
            <p className="text-gray-600">Interface administrateur</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center"
          >
            {showForm ? <X size={20} className="mr-2" /> : <Plus size={20} className="mr-2" />}
            {showForm ? 'Annuler' : 'Nouvel Update'}
          </button>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">
              {editingId ? 'Modifier l\'update' : 'Créer un nouvel update'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Version et Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Version *
                  </label>
                  <input
                    type="text"
                    name="version"
                    value={formData.version}
                    onChange={handleChange}
                    placeholder="v1.0.0"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="majeur">🚀 Majeur</option>
                    <option value="mineur">✨ Mineur</option>
                    <option value="correctif">🔧 Correctif</option>
                  </select>
                </div>
              </div>

              {/* Titre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleChange}
                  placeholder="Titre de la mise à jour"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description générale de la mise à jour"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Nouveautés */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ✨ Nouveautés
                </label>
                {formData.nouveautes.map((item, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange('nouveautes', index, e.target.value)}
                      placeholder="Nouvelle fonctionnalité"
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('nouveautes', index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('nouveautes')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                >
                  + Ajouter une nouveauté
                </button>
              </div>

              {/* Améliorations */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🔧 Améliorations
                </label>
                {formData.ameliorations.map((item, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange('ameliorations', index, e.target.value)}
                      placeholder="Amélioration"
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('ameliorations', index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('ameliorations')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                >
                  + Ajouter une amélioration
                </button>
              </div>

              {/* Corrections */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🐛 Corrections de bugs
                </label>
                {formData.corrections.map((item, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange('corrections', index, e.target.value)}
                      placeholder="Correction de bug"
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('corrections', index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('corrections')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                >
                  + Ajouter une correction
                </button>
              </div>

              {/* Visible */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="visible"
                  checked={formData.visible}
                  onChange={handleChange}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label className="text-sm font-semibold text-gray-700">
                  Visible publiquement
                </label>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center"
                >
                  <Save size={18} className="mr-2" />
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des updates */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {updates.map((update) => (
                <tr key={update._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {update.version}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {update.titre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      update.type === 'majeur' ? 'bg-purple-100 text-purple-800' :
                      update.type === 'mineur' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {update.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(update.datePublication).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      update.visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {update.visible ? 'Visible' : 'Caché'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => toggleVisibility(update._id)}
                        className="text-gray-600 hover:text-gray-900"
                        title={update.visible ? 'Masquer' : 'Afficher'}
                      >
                        {update.visible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <button
                        onClick={() => handleEdit(update)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Modifier"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(update._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {updates.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucun update pour le moment
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminUpdates;